import { fehlerText, planWeg } from "../api/client";
import { Banknote, CalendarClock, Check, ChevronLeft, FileX, Gavel, Plus, type LucideIcon } from "lucide-react";
import { useContext, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { Link, useNavigate } from "react-router";
import { useAkten, useDokumente, useFristErledigt, useFristen, usePersonen } from "../api/abfragen";
import { AnsichtZeile } from "../welt/AnsichtZeile";
import { fristZurPerson, useGewaehltName, usePersonAnsicht } from "../welt/personAnsicht";
import { useNamen } from "../api/namen";
import type { Dokument, Frist } from "../api/typen";
import { FristBadge } from "../design/Badge";
import { Gruppe } from "../design/Gruppe";
import { Kachel } from "../design/Kachel";
import { Knopf } from "../design/Knopf";
import { Fehlertext, Laedt, Leer } from "../design/Leer";
import { personFarbe } from "../design/personFarbe";
import { PersonMarke } from "../design/PersonMarke";
import { Segment } from "../design/Segment";
import { ProfilKnopf } from "../design/Profil";
import { Seite } from "../design/Seite";
import { useToast } from "../design/toastKontext";
import { betrag, datum, FRISTARTEN, tageBis } from "../format";
import { fristAkteZeile, fristTitel, fristWoher } from "./fristWoher";
import { FRIST_TEXTE, useFristBlatt } from "./fristNeu";
import { FristLoeschen, FristNeuBlatt } from "./FristNeuBlatt";
import { Blase } from "../welt/Blase";
import type { SatzTeil } from "../welt/blasenForm";
import { DAUER, kurve, kurveFuer, spiel } from "../welt/bewegung";
import { darf } from "../welt/darf";
import { useWelt } from "../welt/einstellungen";
import { Geist } from "../welt/Geist";
import { GeistEbene } from "../welt/GeistEbene";
import { useGeist } from "../welt/geistKontext";
import { Glas } from "../welt/Glas";
import { SprecherKontext, WeltKontext } from "../welt/kontext";
import { WeltWurzel } from "../welt/WeltWurzel";
import { heuteBerlin } from "../welt/lage";
import { FRISTWORT, fristRechts, satzAntwort } from "../welt/saetze";
import { formFuer, Zustand } from "../welt/Zustand";
import { nachrueckenVormerken } from "../buehne/nachruecken";

/** Symbol je Fristart (Handoff iconFor: Zahlen, Widersprechen, Kuendigen, sonst Termin). */
const SYMBOL: Record<string, LucideIcon> = { zahlen: Banknote, widersprechen: Gavel, kuendigen: FileX };

type Person = { name: string; farbe: string };

/** Detail rechts ab 1280 px: neben der Seitenleiste (240 px) und dem Rand bleiben dann rund 900 px,
 *  genug fuer die Liste und die 360 px des Details. */
const WEIT = "(min-width: 1280px)";
function abonnieren(melden: () => void) {
  const m = window.matchMedia(WEIT);
  m.addEventListener("change", melden);
  return () => m.removeEventListener("change", melden);
}
function useWeit(): boolean {
  return useSyncExternalStore(
    abonnieren,
    () => window.matchMedia(WEIT).matches,
    () => false,
  );
}

/** Fristen (Handoff Schiefer, PgDeadlines): Ueberfaellig, Bald, Spaeter; abhaken mit einem Tipp.
 *  Am Desktop steht rechts das Detail der gewaehlten Frist (aus den Daten der Frist, ihres Briefs und
 *  ihrer Akte; ohne eigene Abfrage). W82 Web-5b (K32, entscheide D2): die Seite steht in einer Geist-Ebene wie die Akten;
 *  der Geist der Titelzeile antwortet auf das Abhaken wie auf der Startseite. */
export function Fristen() {
  return (
    <WeltWurzel kennung="fristen-seite">
      <GeistEbene>
        <FristenInhalt />
      </GeistEbene>
    </WeltWurzel>
  );
}

/** Ziel des Fokus nach dem Löschen der letzten Zeile einer Gruppe: „Neu“ in der Titelzeile. */
const neuKnopf = () => document.querySelector<HTMLElement>('[data-testid="fristen-neu"]');

/** Wie lange die Antwort an seinem Platz steht (wie auf der Startseite, `Posteingang.tsx`). */
const ANTWORT_MS = 3000;

function FristenInhalt() {
  const [ansicht, setAnsicht] = useState<"offen" | "alle">("offen");
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  // W82 Web-5c (K15): Frist von Hand; nach dem Sichern geht der Fokus auf die neue Zeile (ab 1280 px ist sie gewählt)
  const [neu, setNeu] = useState(false);
  const neueFrist = useRef<string | null>(null);
  // W82 Web-5h1 (F2 = A): Blatt „Frist“ für eine bestehende Frist (unter 1280 px aus der Zeile, darüber „Ändern“ im Detail)
  const blatt = useFristBlatt();
  const loeschFrage = useFristBlatt();
  const toast = useToast();
  const { data, error, isPending } = useFristen(ansicht === "offen");
  // nur für die Herkunft (`woher`) und „Aus Brief“; die Person kommt aus dem Feld des Servers (R-P2)
  const { data: briefe } = useDokumente({ pro_seite: 200 });
  const { data: personen } = usePersonen();
  const { data: akten } = useAkten();
  const namen = useNamen();
  const breit = useWeit();
  // W82 Web-5h1 (F1 = C, D-624): der Reiter folgt der Personen-Ansicht wie die Startseite (Regel R-P2, `fristZurPerson`)
  const { person: ansichtPerson } = usePersonAnsicht();
  const gewaehltName = useGewaehltName();
  const liste = (data ?? []).filter((f) => fristZurPerson(f, ansichtPerson));
  function brief(f: Frist): Dokument | undefined {
    return briefe?.eintraege.find((b) => b.id === f.dokument_id);
  }
  function woher(f: Frist): string | undefined {
    const d = brief(f);
    // W73c (D-289 r): ohne Absender der Betreff, sonst das Datum des Briefs; W80: ohne Brief mit Akte „Akte: <Name>“
    return fristWoher(d, d && namen.absender(d.absender_id)) ?? fristAkteZeile(f, akten?.find((a) => a.id === f.akte_id)?.name);
  }
  // Personen-Marken erst ab zwei Personen im Haushalt (Plan 6.6)
  const mehrere = (personen?.length ?? 0) >= 2;
  // W82 Web-5h1 (BS09): Marke und „Für“ aus `person_id` und `gemeinsam` der Frist (Server 5h-S, 5h-S2), nicht aus den ersten
  // 200 Briefen; ohne die Felder (älterer Server) keine Marke
  function person(f: Frist): Person | undefined {
    if (!mehrere) return undefined;
    if (f.gemeinsam || (!f.dokument_id && f.person_id === null)) return { name: "Gemeinsam", farbe: personFarbe(personen, null, true) };
    const name = f.person_id ? namen.person(f.person_id) : undefined;
    return name ? { name, farbe: personFarbe(personen, f.person_id ?? null) } : undefined;
  }
  /** Marke in der Zeile: eine Frist ohne Brief trägt sie nur mit einer Person; „Gemeinsam“ ist dort die Vorgabe (F1 = A) und steht
   *  wie bisher ohne Marke (das Detail nennt „Für: Gemeinsam“) */
  function marke(f: Frist): Person | undefined {
    return !f.dokument_id && !f.person_id ? undefined : person(f);
  }
  const gruppen: [string, Frist[]][] = [
    ["Überfällig", liste.filter((f) => !f.erledigt && tageBis(f.datum) < 0)],
    ["Bald", liste.filter((f) => !f.erledigt && tageBis(f.datum) >= 0 && tageBis(f.datum) <= 7)],
    ["Später", liste.filter((f) => !f.erledigt && tageBis(f.datum) > 7)],
    ["Erledigt", liste.filter((f) => f.erledigt)],
  ];
  const offen = liste.filter((f) => !f.erledigt);
  const ueberfaellig = gruppen[0]?.[1].length ?? 0;
  const auswahl = gruppen.flatMap(([, f]) => f).find((f) => f.id === gewaehlt) ?? gruppen.flatMap(([, f]) => f)[0];

  // ---------- K32: Antwort des Geists nach dem Abhaken (wie Posteingang.tsx `antworten`, ohne Flug) ----------
  const [welt] = useWelt();
  const geist = useGeist();
  const platz = useRef<HTMLSpanElement>(null);
  const [antwort, setAntwort] = useState<{ teile: SatzTeil[]; nr: number } | null>(null);
  const antwortUhr = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(antwortUhr.current), []);
  const { platzMelden } = geist;
  // sein Platz in der Titelzeile: Sicht-Regel und Heimkehr der Ebene
  useLayoutEffect(() => {
    platzMelden(platz.current);
    return () => platzMelden(null);
  }, [platzMelden]);
  /** Nach erfolgreichem Abhaken: „Erledigt. Noch n Fristen stehen an.“ mit `n` = offene Fristen bis 7 Tage der Liste danach
   *  (Überfällig plus Bald); die Seite kennt die Briefe nicht. Bei „Keine Erklärungen“ nur das kurze Leuchten. */
  function antworten(f: Frist, zeile: HTMLElement | null) {
    const frage = { fassung: welt.fassung, praesenz: welt.praesenz, erklaert: tageBis(f.datum) < 0 };
    const n = liste.filter((x) => x.id !== f.id && !x.erledigt && tageBis(x.datum) <= 7).length;
    const satz = satzAntwort("erledigt", { fristen: n, briefe: null });
    if (darf("geste", frage)) hopsen();
    if (darf("satzAntwort", frage)) {
      // Sicht-Regel: außer Sicht kommt er ins Bild (ruhig als Randgeist), sonst spricht er an seinem Platz
      if (!geist.sage(satz, { erklaert: frage.erklaert, ziel: zeile, rand: true })) {
        setAntwort((alt) => ({ teile: satz, nr: (alt?.nr ?? 0) + 1 }));
        clearTimeout(antwortUhr.current);
        antwortUhr.current = setTimeout(() => setAntwort(null), ANTWORT_MS);
      }
      return;
    }
    if (zeile) geist.markiere(zeile, "erledigt");
  }
  function hopsen() {
    spiel(platz.current?.querySelector<HTMLElement>(".geist-sprung"), [{ transform: "none" }, { transform: "translateY(-16px)", offset: 0.4 }, { transform: "none" }], {
      dauer: DAUER.gesten[welt.fassung].hopsen,
      kurve: kurve(kurveFuer("knopf", welt.fassung)),
      art: "lage",
    });
  }

  // W82 Teil 2: Unterseite des Posteingangs („Alle“ im Fristen-Modul) in der W82-Welt, mit Rückweg zum Posteingang
  return (
    <Seite
      titel="Fristen"
      breite="detail"
      // W82-2 N3 (Vorlage R11 N1 „Fristen“): Rückweg als runder Glasknopf „Start“ (das Profil bleibt, Bestand); der Geist sitzt
      // rechts in der Titelzeile; „1 überfällig“ in Koralle; Gruppen mit Abschnittstitel und Anzahl, Zeilen in Glas
      links={
        <span className="fristen-links">
          <ProfilKnopf />
          <Link to="/" className="fristen-zurueck glas">
            <ChevronLeft size={20} strokeWidth={2.2} aria-hidden />
            Start
          </Link>
        </span>
      }
      rechts={
        <button type="button" className="fristen-neu glas" aria-label="Neue Frist" aria-haspopup="dialog" data-testid="fristen-neu" onClick={() => setNeu(true)}>
          <Plus size={18} strokeWidth={2} aria-hidden />
          <span className="fristen-neu-text">Neu</span>
        </button>
      }
      kopf={
        <div className="fristen-kopf">
          {/* K32: der Geist der Titelzeile spricht (Sprecher-Regel: Figur und Blase in einer Geist-Szene) */}
          <SprecherKontext.Provider value="szene">
            <div className="fristen-rede" data-geist-szene="" data-unterwegs={geist.unterwegs ? "" : undefined} data-testid="fristen-geist">
              <span ref={platz} className="fristen-geist" data-geist-platz="" aria-hidden="true">
                <Geist groesse={54} />
              </span>
              {antwort && <FristenAntwort key={antwort.nr} satz={antwort.teile} />}
            </div>
          </SprecherKontext.Provider>
          {data && liste.length > 0 && (
            <p className="fristen-unterzeile t-footnote -mt-1 text-pg-text-2">
              {offen.length} offen
              {ueberfaellig > 0 && (
                <>
                  {" · "}
                  <span className="fristen-warn">{ueberfaellig} überfällig</span>
                </>
              )}
            </p>
          )}
          <Segment
            label="Ansicht"
            wert={ansicht}
            onChange={setAnsicht}
            optionen={[
              { wert: "offen", label: "Offen" },
              { wert: "alle", label: "Alle" },
            ]}
          />
        </div>
      }
    >
      <div className="flex items-start gap-6">
        <div className="min-w-0 flex-1">
          {/* W82 Web-5h1 (F1 = C): „Ansicht: Ben“ mit „Alle zeigen“ über den Gruppen, wie auf der Startseite */}
          <AnsichtZeile />
          {isPending && <Laedt />}
          {error && <Fehlertext fehler={error} />}
          {data && liste.length === 0 &&
            (gewaehltName ? (
              <Leer icon={CalendarClock} titel={FRIST_TEXTE.leerTitel(gewaehltName)}>
                {FRIST_TEXTE.leerText}
              </Leer>
            ) : (
              <Leer icon={CalendarClock} titel="Keine offenen Fristen">Fristen aus Briefen erscheinen hier, sobald sie bestätigt sind.</Leer>
            ))}
          {gruppen.map(([titel, fristen]) =>
            fristen.length ? (
              <Gruppe
                key={titel}
                titel={
                  <>
                    {titel} <span className="gruppe-anzahl font-medium text-pg-text-2">{fristen.length}</span>
                  </>
                }
                titelLesetext={`${titel} · ${fristen.length}`}
                id={`fristen-${titel}`}
              >
                {fristen.map((f) => (
                  <FristZeile
                    key={f.id}
                    frist={f}
                    woher={woher(f)}
                    person={marke(f)}
                    gewaehlt={breit && auswahl?.id === f.id}
                    onWaehlen={breit ? () => setGewaehlt(f.id) : undefined}
                    onOeffnen={() => blatt.oeffnen(f)}
                    onUmgeschaltet={(erledigt, zeile) => {
                      if (erledigt) antworten(f, zeile);
                    }}
                  />
                ))}
              </Gruppe>
            ) : null,
          )}
        </div>
        {breit && auswahl && (
          <FristDetail
            frist={auswahl}
            brief={brief(auswahl)}
            woher={woher(auswahl)}
            person={person(auswahl)}
            onUmgeschaltet={(erledigt) => {
              if (erledigt) antworten(auswahl, document.querySelector(`[data-frist-zeile="${auswahl.id}"]`));
            }}
            onAendern={() => blatt.oeffnen(auswahl)}
            onLoeschen={() => loeschFrage.oeffnen(auswahl)}
          />
        )}
      </div>
      {/* Z16: „Löschen“ im Detail fragt ohne Blatt; danach wählt das Detail die nachrückende Zeile, der Fokus geht auf sie */}
      <FristLoeschen
        frist={loeschFrage.frist}
        offen={loeschFrage.offen}
        leerZiel={neuKnopf}
        onSchliessen={loeschFrage.schliessen}
        onGeloescht={(nachbarn) => {
          loeschFrage.schliessen();
          setGewaehlt(nachbarn[0] ?? null);
        }}
        onFehler={(e) => toast(fehlerText(e), planWeg(e))}
      />
      <FristNeuBlatt
        offen={blatt.offen}
        frist={blatt.frist}
        brief={blatt.frist && brief(blatt.frist)}
        woher={blatt.frist && woher(blatt.frist)}
        onSchliessen={blatt.schliessen}
        leerZiel={neuKnopf}
        // R1 W82-Web-5h1: ab 1280 px steht danach die nachrückende Frist im Detail, dorthin geht auch der Fokus
        onGeloescht={(nachbarn) => setGewaehlt(nachbarn[0] ?? null)}
      />
      <FristNeuBlatt
        offen={neu}
        onSchliessen={() => setNeu(false)}
        onAngelegt={(f) => {
          neueFrist.current = f.id;
          if (breit) setGewaehlt(f.id);
        }}
        fokusNachher={() => {
          const id = neueFrist.current;
          neueFrist.current = null;
          const zeile = id ? document.querySelector<HTMLElement>(`[data-frist-zeile="${id}"]`) : null;
          // R1 (Befund C): das Blatt fokussiert ohne Rollen; mobil lag die neue Zeile sonst unter der Tab-Leiste oder unterhalb
          // des Fensters. Mittig ins Bild, frei von Kopf und Leiste, ohne Bewegung
          zeile?.scrollIntoView?.({ block: "center" });
          // die Fläche der Zeile (Brief, Detail) oder die Zeile selbst; Abhaken wäre ein Tipp zu weit
          return zeile?.querySelector<HTMLElement>(".fz-flaeche:is(a, button)") ?? zeile;
        }}
      />
    </Seite>
  );
}

/** K32: die Antwort wächst aus ihrer Spitze unter ihm (Spitzen-Regel; Blase 640 ms wie die Akten-Rede, ruhig ohne Feder). */
function FristenAntwort(props: { satz: SatzTeil[] }) {
  const [welt] = useWelt();
  const blase = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    spiel(blase.current, [{ transform: "translate(12px, -8px) scale(0.7)", opacity: 0 }, { transform: "none", opacity: 1 }], {
      dauer: 640,
      kurve: kurve(kurveFuer("blase", welt.fassung)),
      art: "lage",
    });
    // nur beim Erscheinen; eine neue Antwort hängt die Blase neu ein (Schlüssel)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <Blase ref={blase} form="titel" satz={props.satz} className="fristen-blase" />;
}

/** Abhaken bzw. wieder oeffnen einer Frist (Zeile und Detail teilen den Weg). `nachher` meldet den Erfolg (W82: Antwort
 *  des Geists auf der Startseite); der Bestandstext „Erledigt.“ bzw. „Wieder offen.“ bleibt. */
function useUmschalten(f: Frist, nachher?: (erledigt: boolean) => void) {
  const erledigen = useFristErledigt();
  const toast = useToast();
  return {
    laeuft: erledigen.isPending,
    umschalten: () =>
      erledigen.mutate(
        { id: f.id, erledigt: !f.erledigt },
        {
          onSuccess: () => {
            toast(f.erledigt ? "Wieder offen." : "Erledigt.");
            nachher?.(!f.erledigt);
          },
          onError: (e) => toast(fehlerText(e), planWeg(e)),
        },
      ),
  };
}

/** Form je Zustand in der W82-Zeile (R11 `.glyph`): erledigt ein Punkt in Mint (W82 Web-5c: gemeinsam mit dem Dokument). */
const FORM_WELT = formFuer;

/** Fristzeile mit Link zum Brief und Abhaken; auch in der Akte (Fristen der Akte, W4 Optional).
 *  Mit `onWaehlen` (Desktop in den Fristen) waehlt ein Tipp auf die Zeile das Detail rechts, der Brief
 *  oeffnet sich dort. */
export function FristZeile(props: {
  frist: Frist;
  woher?: string | undefined;
  person?: Person | undefined;
  gewaehlt?: boolean | undefined;
  onWaehlen?: (() => void) | undefined;
  /** W82 Web-5h1 (F2 = A): ohne `onWaehlen` öffnet die Fläche der Zeile das Blatt „Frist“ (Lesetext Z12 „…, Details“) statt zum
   *  Brief zu führen */
  onOeffnen?: (() => void) | undefined;
  /** W82: nach dem Abhaken (Antwort des Geists) */
  onUmgeschaltet?: ((erledigt: boolean, zeile: HTMLElement | null) => void) | undefined;
  /** W82 Teil 3 (Vormerkung R2 Web-2, FW12): der Lesetext des Abhakens nennt das sichtbare Fristwort („Zahlung erledigt“
   *  neben „Zahlung“); in der Akte. Startseite und Fristen-Seite behalten „Zahlen erledigt“ (Tests der Welle 2, STOP). */
  lesetextFristwort?: boolean | undefined;
}) {
  const welt = useContext(WeltKontext);
  if (welt) return <FristZeileWelt {...props} />;
  return <FristZeileSchiefer {...props} />;
}

function FristZeileSchiefer(props: Parameters<typeof FristZeile>[0]) {
  const f = props.frist;
  const { woher, person, onWaehlen, onOeffnen } = props;
  const { umschalten } = useUmschalten(f);
  // W80 (D-352 F1): eine eigene Frist mit Titel nennt ihn zuerst; Art und Betrag stehen dann vor der Herkunft darunter
  const titel = fristTitel(f);
  const art = (
    <>
      {FRISTARTEN[f.art]}
      {f.betrag && <span className="t-zahl"> · {betrag(f.betrag)}</span>}
    </>
  );
  const inhalt = (
    <>
      <Kachel schluessel={null} icon={SYMBOL[f.art] ?? CalendarClock} />
      <span className="zeile-text flex min-w-0 flex-1 flex-col">
        <span className="t-body t-body-medium flex flex-wrap items-center gap-x-2">
          <span className={titel ? "min-w-0 break-words hyphens-auto" : undefined}>{titel ?? art}</span>
          {person && <PersonMarke name={person.name} farbe={person.farbe} groesse="klein" />}
        </span>
        {titel ? (
          // W82 Web-8 W8-2 (R-1 NR-1, D-1559): die Herkunft wird nicht gekürzt, sie bricht um wie der Titel
          <span className="t-subheadline break-words hyphens-auto text-pg-text-2">
            {art}
            {woher && ` · ${woher}`}
          </span>
        ) : (
          woher && <span className="t-subheadline break-words hyphens-auto text-pg-text-2">{woher}</span>
        )}
        <span className="zeile-paar mt-1 flex">
          <FristBadge datum={f.datum} erledigt={Boolean(f.erledigt)} />
        </span>
      </span>
    </>
  );
  const flaeche = "flex min-w-0 flex-1 items-start gap-3 py-3 pl-4 hover:bg-pg-surface-2";
  return (
    <li className="zeile zeile-kachel relative">
      {/* Innere Ebene: die Container-Abfrage greift nur auf Kinder des Containers (li). Die Markierung der
          gewaehlten Zeile liegt hier, damit sie bis hinter den Erledigt-Knopf reicht (Verifier W22b, F5) */}
      <div className={`zeile-flex flex items-center gap-2 pr-4 ${props.gewaehlt ? "bg-pg-surface-2" : ""}`}>
        {!onWaehlen && onOeffnen ? (
          <button type="button" className={`${flaeche} text-left`} aria-haspopup="dialog" onClick={onOeffnen}>
            {inhalt}
            <span className="sr-only">{FRIST_TEXTE.details}</span>
          </button>
        ) : f.dokument_id ? (
          <Link
            to={`/dokument/${f.dokument_id}`}
            className={flaeche}
            aria-current={props.gewaehlt ? "true" : undefined}
            onClick={
              onWaehlen
                ? (e) => {
                    e.preventDefault();
                    onWaehlen();
                  }
                : undefined
            }
          >
            {inhalt}
          </Link>
        ) : onWaehlen ? (
          <button type="button" className={`${flaeche} text-left`} aria-current={props.gewaehlt ? "true" : undefined} onClick={onWaehlen}>
            {inhalt}
          </button>
        ) : (
          <div className={flaeche}>{inhalt}</div>
        )}
        <span className="zeile-hinten flex shrink-0">
          {/* Name und Rolle bleiben (Specs, Screenreader); der Tooltip nennt den Handoff-Text. Rand wie ein
              Kontrollkaestchen im Feldrand: >= 3:1 auch auf der gewaehlten Zeile (W22d-w4) */}
          <button
            type="button"
            aria-label={f.erledigt ? `${FRISTARTEN[f.art]} wieder öffnen` : `${FRISTARTEN[f.art]} erledigt`}
            title={f.erledigt ? "Wieder öffnen" : "Als erledigt markieren"}
            aria-pressed={Boolean(f.erledigt)}
            onClick={umschalten}
            className={`grid size-(--touch) shrink-0 place-items-center rounded-md ${f.erledigt ? "bg-pg-ok-soft text-pg-ok" : "border border-pg-field-border text-pg-text-2 hover:bg-pg-surface-2 hover:text-pg-accent-text"}`}
          >
            <Check size={20} strokeWidth={1.5} aria-hidden />
          </button>
        </span>
      </div>
    </li>
  );
}

/** W82-Gestalt der Fristzeile (DESIGN-W82 „Fristen-Modul“, Liste; Vorlage R11 N1 `.frist`): mindestens 66 hoch, Raster
 *  44 / Text / rechts; links der Kreis zum Abhaken (26 in 44, Ring in der Zustandsfarbe, Name und Rolle des Bestands), in
 *  der Mitte das Fristwort und die Herkunft (zwei Zeilen, `.zeile-text` wie bisher), rechts der Betrag oder ohne Betrag
 *  der Tag und darunter der Zustand mit Form und Wort (FW12 wie iOS: „Zahlung“, „seit 3 Tagen“, „in 5 Tagen“, „bezahlt
 *  14.10.“). Abgehakt: Kreis in Mint mit Haken, Titel durchgestrichen, Betrag in Nebentext. Eine eigene Frist nennt zuerst
 *  ihren Titel, darunter Fristwort und Herkunft (W80, D-352 F1). */
function FristZeileWelt(props: Parameters<typeof FristZeile>[0]) {
  const f = props.frist;
  const { woher, person, onWaehlen, onOeffnen } = props;
  const { umschalten } = useUmschalten(f, (erledigt) => props.onUmgeschaltet?.(erledigt, document.querySelector(`[data-frist-zeile="${f.id}"]`)));
  const titel = fristTitel(f);
  const wort = FRISTWORT[f.art] ?? FRISTWORT.sonstige;
  const lesewort = wort;
  // eine eigene Frist mit Titel nennt darunter Fristwort und Betrag wie der Bestand (W80), rechts steht dann ihr Tag
  const unter = titel ? [`${wort}${f.betrag ? ` · ${betrag(f.betrag)}` : ""}`, woher].filter(Boolean).join(" · ") : woher;
  const rechts = fristRechts(titel ? { ...f, betrag: null } : f, heuteBerlin(), betrag);
  const zustand = rechts.zustand;
  const flaeche = "fz-flaeche";
  const inhalt = (
    <span className="zeile-text">
      <span className="fz-titel w-zeilen-titel">
        <span className={titel ? "min-w-0 break-words hyphens-auto" : undefined}>{titel ?? wort}</span>
        {person && <PersonMarke name={person.name} farbe={person.farbe} groesse="klein" />}
      </span>
      {unter && <span className="fz-unter w-nebentext">{unter}</span>}
    </span>
  );
  return (
    // tabIndex -1: Ziel des Fokus nach „Neue Frist“, wenn die Zeile keine eigene Fläche zum Tippen hat (kein Halt beim Tabulator)
    <li className="zeile frist-zeile" tabIndex={-1} data-frist-zeile={f.id} data-zustand={zustand} data-erledigt={f.erledigt ? "" : undefined}>
      <div className={`zeile-flex fz-raster ${props.gewaehlt ? "fz-gewaehlt" : ""}`}>
        <button
          type="button"
          className="abhaken"
          data-zustand={zustand === "erledigt" ? "spaeter" : zustand}
          aria-label={f.erledigt ? `${lesewort} wieder öffnen` : `${lesewort} erledigt`}
          title={f.erledigt ? "Wieder öffnen" : "Als erledigt markieren"}
          aria-pressed={Boolean(f.erledigt)}
          onClick={umschalten}
          onPointerDown={abhakenGetippt}
        >
          <span className="abhaken-kreis">{f.erledigt && <Check size={16} strokeWidth={1.75} aria-hidden />}</span>
        </button>
        {!onWaehlen && onOeffnen ? (
          // Z12: der Inhalt der Zeile wie bisher, dazu „, Details“ (das Blatt „Frist“ öffnet sich); als Name gesetzt, weil die
          // Blöcke der Fläche sonst vor dem Komma ein Leerzeichen einfügten
          <button
            type="button"
            className={`${flaeche} text-left`}
            aria-haspopup="dialog"
            aria-label={`${[titel ?? wort, person?.name, unter].filter(Boolean).join(" ")}${FRIST_TEXTE.details}`}
            onClick={onOeffnen}
          >
            {inhalt}
          </button>
        ) : f.dokument_id ? (
          <Link
            to={`/dokument/${f.dokument_id}`}
            className={flaeche}
            aria-current={props.gewaehlt ? "true" : undefined}
            onClick={
              onWaehlen
                ? (e) => {
                    e.preventDefault();
                    onWaehlen();
                  }
                : undefined
            }
          >
            {inhalt}
          </Link>
        ) : onWaehlen ? (
          <button type="button" className={`${flaeche} text-left`} aria-current={props.gewaehlt ? "true" : undefined} onClick={onWaehlen}>
            {inhalt}
          </button>
        ) : (
          <div className={flaeche}>{inhalt}</div>
        )}
        <span className="fz-rechts">
          <span className="fz-betrag w-betrag-frist">{rechts.oben}</span>
          <span className="fz-zustand frist-badge w-zustand" data-zustand={zustand}>
            <Zustand form={FORM_WELT(zustand)} />
            {rechts.text}
          </span>
        </span>
      </div>
    </li>
  );
}

/** Detail rechts (nur Desktop): Frist, Brief und Akte. Die Daten kommen aus der Fristliste, der
 *  Briefliste und den Akten; die IBAN liefert die API nicht, „Verschieben“ gibt es nicht. W82 Web-5c (K16, entscheide E2):
 *  Glas-Modul der Welt statt der dunklen Karte, Titel wie ein Modul, Zustand mit Form und Wort (`fristRechts`), Angaben als
 *  Glas-Liste in zwei Spalten wie die Akte, Knöpfe der Welt (hell, leise); Angaben, kein Satz des Geists. */
function FristDetail(props: {
  frist: Frist;
  brief: Dokument | undefined;
  woher: string | undefined;
  person: Person | undefined;
  /** W82 Web-5b (K32): nach dem Abhaken im Detail antwortet der Geist wie nach dem Abhaken in der Zeile */
  onUmgeschaltet?: (erledigt: boolean) => void;
  /** W82 Web-5h1 (Z16): „Ändern“ öffnet das Blatt „Frist“, „Löschen“ die Rückfrage, für jede Frist */
  onAendern: () => void;
  onLoeschen: () => void;
}) {
  const f = props.frist;
  const d = props.brief;
  const { data: akten } = useAkten();
  const navigieren = useNavigate();
  const { umschalten, laeuft } = useUmschalten(f, props.onUmgeschaltet);
  const akte = f.akte_id ? akten?.find((a) => a.id === f.akte_id) : undefined;
  const r = fristRechts(f, heuteBerlin(), betrag);
  const wort = FRISTWORT[f.art] ?? FRISTWORT.sonstige;
  // W80: eine eigene Frist mit Titel trägt ihn als Überschrift, das Fristwort steht dann in den Angaben
  const eigener = fristTitel(f);
  const herkunft = d ? undefined : props.woher;
  return (
    <Glas art="modul" als="aside" name="Ausgewählte Frist" className="frist-detail sticky top-20 mt-4 w-2/5 shrink-0">
      <div className="frist-detail-kopf">
        <h2 className="w-modul-titel break-words hyphens-auto">{eigener ?? wort}</h2>
        <span className="fz-zustand frist-badge w-zustand" data-zustand={r.zustand}>
          <Zustand form={formFuer(r.zustand)} />
          {r.text}
        </span>
      </div>
      {/* items-baseline: die Bezeichnung steht auf einer Linie mit dem Wert, auch neben einem Link mit 44 px Trefferfläche */}
      <dl className="frist-detail-angaben">
        {eigener && (
          <div>
            <dt>Art</dt>
            <dd>{wort}</dd>
          </div>
        )}
        {f.betrag && (
          <div>
            <dt>Betrag</dt>
            <dd className="t-zahl">{betrag(f.betrag)}</dd>
          </div>
        )}
        <div>
          <dt>Fällig</dt>
          <dd className="t-zahl">{datum(f.datum)}</dd>
        </div>
        {d && (
          <div>
            <dt>Aus Brief</dt>
            <dd>
              <Link to={`/dokument/${d.id}`} className="frist-detail-verweis">
                {d.dokumentart ?? d.betreff ?? "Brief"} vom {datum(d.datum ?? d.erstellt)}
              </Link>
              {props.woher && <span className="frist-detail-neben"> · {props.woher}</span>}
            </dd>
          </div>
        )}
        {akte && (
          <div>
            <dt>Akte</dt>
            <dd>
              <Link to={`/akten/${akte.id}`} className="frist-detail-verweis">
                {akte.name}
              </Link>
            </dd>
          </div>
        )}
        {herkunft && !akte && (
          <div>
            <dt>Herkunft</dt>
            <dd>{herkunft}</dd>
          </div>
        )}
        {props.person && (
          <div>
            <dt>Für</dt>
            <dd>{props.person.name}</dd>
          </div>
        )}
      </dl>
      <div className="frist-detail-knoepfe">
        <Knopf icon={Check} disabled={laeuft} onClick={umschalten}>
          {f.erledigt ? "Wieder öffnen" : "Erledigt"}
        </Knopf>
        {f.dokument_id && (
          <Knopf art="sekundaer" onClick={() => void navigieren(`/dokument/${f.dokument_id}`)}>
            Brief öffnen
          </Knopf>
        )}
        <Knopf art="sekundaer" aria-haspopup="dialog" onClick={props.onAendern}>
          {FRIST_TEXTE.aendern}
        </Knopf>
        <Knopf art="rot" aria-haspopup="dialog" onClick={props.onLoeschen}>
          {FRIST_TEXTE.loeschenDetail}
        </Knopf>
      </div>
    </Glas>
  );
}

/** W82 Design-Web-1 (Teil B c): ein Tipp (nicht die Tastatur) auf den Kreis einer Fristen-Zeile: die Welle darf einmal laufen
 *  (`data-getippt`, buehne.css), und die Fristen-Zeilen merken sich ihre Lage, damit sie nachrücken (buehne/nachruecken.ts). */
function abhakenGetippt(e: React.PointerEvent<HTMLButtonElement>) {
  const knopf = e.currentTarget;
  // Zurücknehmen (der Kreis ist schon abgehakt): Nachrücken wie gehabt, aber keine Welle und kein Haken-Sprung (R2: Welle bei „Erledigt“ zurück)
  if (knopf.getAttribute("aria-pressed") !== "true") knopf.dataset.getippt = "";
  nachrueckenVormerken(knopf);
  setTimeout(() => delete knopf.dataset.getippt, 1600);
}
